import type { PixelEvent } from '@/events';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import { BatchManager } from './batch/BatchManager';
import {
  createCompressionStrategy,
  getCompressionDebugState,
} from './compression';
import type { CompressionDebugState } from './compression';
import { CoordinationManager } from './coordination';
import type { CoordinationDebugState } from './coordination';
import { DeadLetterManager } from './dead-letter';
import type { DeadLetterDebugState } from './dead-letter';
import { TransportDelivery } from './delivery/TransportDelivery';
import { FlushManager } from './flush/FlushManager';
import { OfflineDetector } from './network/OfflineDetector';
import { OrderingManager } from './ordering';
import type { OrderingDebugState } from './ordering';
import { TransportPipeline } from './pipeline/TransportPipeline';
import { PersistentQueue } from './queue/PersistentQueue';
import type { IEventQueue } from './queue/types';
import { MemoryQueue } from './queue/MemoryQueue';
import { createQueueStorageFromConfig } from './queue/storage';
import { ReplayManager } from './replay';
import type { ReplayDebugState } from './replay';
import {
  BackpressureManager,
  CircuitBreaker,
  CircuitBreakerSender,
} from './resilience';
import type {
  BackpressureDebugState,
  CircuitBreakerMetrics,
} from './resilience';
import { RetryEngine } from './retry/RetryEngine';
import { EventPayloadBuilder } from './sender/EventPayloadBuilder';
import { createEventSender } from './sender/EventSenderFactory';
import { HttpSender } from './sender/HttpSender';
import { TestSender } from './sender/TestSender';
import type { EventSender } from './sender/types';
import { TimedSender } from './sender/TimedSender';

export type TransportAdvancedDebugState = {
  readonly storage: {
    readonly adapter: string;
    readonly persistenceEnabled: boolean;
    readonly recordCount: number;
  };
  readonly replay: ReplayDebugState;
  readonly ordering: OrderingDebugState;
  readonly coordination: CoordinationDebugState;
  readonly circuitBreaker: CircuitBreakerMetrics;
  readonly backpressure: BackpressureDebugState;
  readonly deadLetter: DeadLetterDebugState;
  readonly compression: CompressionDebugState;
  readonly senderMode: 'test' | 'internal' | 'external' | 'injected';
};

export type TransportDebugState = {
  readonly queueSize: number;
  readonly queueContents: readonly PixelEvent[];
  readonly pipelineSteps: readonly string[];
  readonly senderLogs: readonly string[];
  readonly sentCount: number;
  readonly delivery: ReturnType<TransportDelivery['getDebugState']> | null;
  readonly sender: Record<string, unknown> | null;
  readonly advanced: TransportAdvancedDebugState | null;
};

export class TransportEngine {
  private readonly context: PixelRuntimeContext;

  private readonly queue: IEventQueue;

  private readonly persistentQueue: PersistentQueue | null;

  private readonly pipeline: TransportPipeline;

  private readonly sender: EventSender;

  private readonly testSender: TestSender | null;

  private readonly httpSender: HttpSender | null;

  private readonly delivery: TransportDelivery;

  private readonly timedSender: TimedSender | null;

  private readonly replayManager: ReplayManager | null;

  private readonly orderingManager: OrderingManager;

  private readonly coordinationManager: CoordinationManager;

  private readonly circuitBreaker: CircuitBreaker;

  private readonly backpressureManager: BackpressureManager;

  private readonly deadLetterManager: DeadLetterManager;

  private readonly senderMode: 'test' | 'internal' | 'external' | 'injected';

  private active = false;

  constructor(
    context: PixelRuntimeContext,
    queue?: IEventQueue,
    sender?: EventSender
  ) {
    this.context = context;
    const transportConfig = context.config.transport;

    this.orderingManager = new OrderingManager({
      enabled: transportConfig.ordering.enabled,
    });

    this.backpressureManager = new BackpressureManager({
      enabled: transportConfig.backpressure.enabled,
      maxQueueSize: transportConfig.backpressure.maxQueueSize,
      policy: transportConfig.backpressure.policy,
      sampleRate: transportConfig.backpressure.sampleRate,
    });

    this.deadLetterManager = new DeadLetterManager({
      enabled: transportConfig.deadLetter.enabled,
      maxEntries: transportConfig.deadLetter.maxEntries,
    });

    this.circuitBreaker = new CircuitBreaker({
      enabled: transportConfig.circuitBreaker.enabled,
      failureThreshold: transportConfig.circuitBreaker.failureThreshold,
      resetTimeoutMs: transportConfig.circuitBreaker.resetTimeoutMs,
      halfOpenMaxAttempts: transportConfig.circuitBreaker.halfOpenMaxAttempts,
    });

    this.coordinationManager = new CoordinationManager(
      { enabled: transportConfig.coordination.enabled },
      context.config.identity.workspaceId || 'pixel'
    );

    if (queue) {
      this.queue = queue;
      this.persistentQueue = null;
    } else if (transportConfig.persistence.enabled) {
      const storage = createQueueStorageFromConfig(context.config);
      this.persistentQueue = new PersistentQueue(
        storage,
        this.orderingManager,
        this.backpressureManager
      );
      this.queue = this.persistentQueue;
    } else {
      this.persistentQueue = null;
      this.queue = new MemoryQueue();
    }

    this.pipeline = TransportPipeline.createDefault(this.queue);

    const compression = createCompressionStrategy(transportConfig.compression);
    const payloadBuilder = new EventPayloadBuilder(context);

    const resolved = sender
      ? null
      : createEventSender({
          senderConfig: transportConfig.sender,
          batchConfig: transportConfig.batch,
          timeoutMs: transportConfig.timeout.timeoutMs,
          payloadBuilder,
          compression,
          logger: context.logger,
        });

    this.senderMode = sender ? 'injected' : resolved!.mode;
    this.testSender =
      sender instanceof TestSender ? sender : (resolved?.testSender ?? null);
    this.httpSender =
      sender instanceof HttpSender ? sender : (resolved?.httpSender ?? null);

    let eventSender: EventSender = sender ?? resolved!.sender;

    if (transportConfig.circuitBreaker.enabled) {
      eventSender = new CircuitBreakerSender(this.circuitBreaker, eventSender);
    }

    let timedSender: TimedSender | null = null;

    if (transportConfig.timeout.enabled) {
      timedSender = new TimedSender(eventSender, {
        timeoutMs: transportConfig.timeout.timeoutMs,
      });
      eventSender = timedSender;
    }

    this.timedSender = timedSender;
    this.sender = eventSender;

    if (this.persistentQueue) {
      this.replayManager = new ReplayManager(
        { enabled: transportConfig.replay.enabled },
        this.persistentQueue
      );
      this.replayManager.setReplayHandler(() => this.forwardQueuedEvents());
    } else {
      this.replayManager = null;
    }

    const batchManager = new BatchManager();
    const flushManager = new FlushManager({
      flushIntervalMs: transportConfig.flush.enabled
        ? transportConfig.flush.flushIntervalMs
        : 0,
      flushOnDestroy: transportConfig.flush.flushOnDestroy,
      prepareUnloadHooks:
        transportConfig.flush.enabled &&
        transportConfig.flush.prepareUnloadHooks,
    });
    const retryEngine = new RetryEngine(
      {
        maxAttempts: transportConfig.retry.enabled
          ? transportConfig.retry.maxAttempts
          : 1,
        baseDelayMs: transportConfig.retry.baseDelayMs,
        maxDelayMs: transportConfig.retry.maxDelayMs,
        multiplier: transportConfig.retry.multiplier,
      },
      context.logger
    );
    const offlineDetector = new OfflineDetector();

    this.delivery = new TransportDelivery({
      batchManager,
      flushManager,
      retryEngine,
      offlineDetector,
      sender: this.sender,
      config: {
        batch: { ...transportConfig.batch },
        timeoutMs: transportConfig.timeout.timeoutMs,
      },
      logger: context.logger,
      abortPending: () => timedSender?.abortPending(),
      onPermanentFailure: transportConfig.deadLetter.enabled
        ? (events, error) => {
            this.deadLetterManager.move(
              events,
              'max_retries_exceeded',
              transportConfig.retry.enabled
                ? transportConfig.retry.maxAttempts
                : 1,
              error instanceof Error ? error.message : String(error)
            );
          }
        : undefined,
    });
  }

  async init(): Promise<void> {
    if (this.active) return;

    if (this.persistentQueue) {
      await this.persistentQueue.init();
    }

    this.coordinationManager.init(isLeader => {
      if (isLeader) {
        void this.replayManager?.replay('reconnect');
        this.forwardQueuedEvents();
      }
    });

    this.delivery?.init();

    if (this.replayManager) {
      await this.replayManager.replay('init');
    }

    this.active = true;
    this.context.logger.debug('[TransportEngine] Initialized');
  }

  destroy(): void {
    if (!this.active) return;

    this.timedSender?.abortPending();
    this.delivery?.destroy();
    this.coordinationManager.destroy();

    if (this.persistentQueue) {
      this.persistentQueue.destroy();
    } else {
      this.queue.clear();
    }

    this.testSender?.clear();
    this.httpSender?.clear();
    this.active = false;
    this.context.logger.debug('[TransportEngine] Destroyed');
  }

  isActive(): boolean {
    return this.active;
  }

  handleEvent(event: PixelEvent): void {
    if (!this.active) return;

    const result = this.pipeline.process(event);
    if (!result.event) {
      this.context.logger.debug('[TransportEngine] Event dropped by pipeline');
      return;
    }

    if (this.coordinationManager.shouldPersistOnly()) {
      return;
    }

    this.forwardQueuedEvents();
  }

  manualFlush(): void {
    this.delivery?.flush();
  }

  simulateOffline(offline: boolean): void {
    this.delivery?.getOfflineDetector().simulateOffline(offline);
    if (!offline) {
      void this.replayManager?.replay('reconnect');
      this.forwardQueuedEvents();
    }
  }

  configureTestSender(options: Parameters<TestSender['configure']>[0]): void {
    this.testSender?.configure(options);
  }

  async simulateReload(): Promise<void> {
    await this.replayManager?.replay('reload');
    this.forwardQueuedEvents();
  }

  async simulateRestart(): Promise<void> {
    await this.replayManager?.replay('restart');
    this.forwardQueuedEvents();
  }

  forceCircuitOpen(): void {
    this.circuitBreaker.forceOpen();
  }

  forceCircuitClose(): void {
    this.circuitBreaker.forceClose();
  }

  async manualReplay(): Promise<number> {
    const count = (await this.replayManager?.replay('manual')) ?? 0;
    this.forwardQueuedEvents();
    return count;
  }

  replayDeadLetter(): PixelEvent[] {
    const events = this.deadLetterManager.replayAll();
    if (events.length > 0) {
      for (const event of events) {
        this.queue.enqueue(event);
      }
      this.forwardQueuedEvents();
    }
    return events;
  }

  clearDeadLetter(): void {
    this.deadLetterManager.clear();
  }

  exportQueue(): string {
    if (this.persistentQueue) {
      return JSON.stringify(this.persistentQueue.exportRecords(), null, 2);
    }
    return JSON.stringify(this.queue.getAll(), null, 2);
  }

  importQueue(raw: string): number {
    const parsed = JSON.parse(raw) as unknown;

    if (this.persistentQueue && Array.isArray(parsed) && parsed[0]?.event) {
      this.persistentQueue.importRecords(parsed);
      return parsed.length;
    }

    if (Array.isArray(parsed)) {
      this.queue.clear();
      for (const event of parsed as PixelEvent[]) {
        this.queue.enqueue(event);
      }
      return parsed.length;
    }

    return 0;
  }

  getQueue(): IEventQueue {
    return this.queue;
  }

  getPipelineSteps(): readonly string[] {
    return this.pipeline.getLastSteps();
  }

  getDebugState(): TransportDebugState {
    const senderState =
      this.httpSender?.getState() ?? this.testSender?.getState() ?? null;

    return {
      queueSize: this.queue.size(),
      queueContents: this.queue.getAll(),
      pipelineSteps: this.pipeline.getLastSteps(),
      senderLogs: senderState?.logs ?? [],
      sentCount: senderState?.sentCount ?? 0,
      delivery: this.delivery?.getDebugState() ?? null,
      sender: senderState as Record<string, unknown> | null,
      advanced: {
        storage: {
          adapter:
            this.persistentQueue?.getStorageAdapterName() ?? 'MemoryQueue',
          persistenceEnabled: this.persistentQueue !== null,
          recordCount:
            this.persistentQueue?.getRecords().length ?? this.queue.size(),
        },
        replay: this.replayManager?.getDebugState() ?? {
          enabled: false,
          lastTrigger: null,
          lastReplayAt: null,
          totalReplayed: 0,
          pendingCount: 0,
        },
        ordering: this.orderingManager.getDebugState(this.queue.size()),
        coordination: this.coordinationManager.getDebugState(),
        circuitBreaker: this.circuitBreaker.getMetrics(),
        backpressure: this.backpressureManager.getDebugState(this.queue.size()),
        deadLetter: this.deadLetterManager.getDebugState(),
        compression: getCompressionDebugState(
          this.context.config.transport.compression,
          createCompressionStrategy(this.context.config.transport.compression),
          this.httpSender?.getState().compression ?? null
        ),
        senderMode: this.senderMode,
      },
    };
  }

  private forwardQueuedEvents(): void {
    if (!this.coordinationManager.canSend()) return;

    const events: PixelEvent[] = [];

    while (!this.queue.isEmpty()) {
      const event = this.queue.dequeue();
      if (event) events.push(event);
    }

    if (events.length === 0) return;

    this.delivery.ingest(events);
  }
}
